import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import type { PickInput } from '@lifetimeline/shared';

import {
  PickGrid,
  PickGridAddCell,
  PickGridCell,
  PickGridGhostCell,
  SortablePickGrid,
} from '../components/PickGrid.js';
import { Button } from '../components/Button.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { Layout } from '../components/Layout.js';
import { useToast } from '../components/Toast.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { usePickFlow } from '../features/pick/PickFlow.js';
import { useI18n } from '../i18n/index.js';
import { categoryName, loadCatalog, type LoadedCatalog } from '../lib/catalog.js';
import {
  putLocalPick,
  readLocalPicks,
  removeLocalPick,
  reorderLocalPicks,
  type LocalPick,
} from '../lib/localPicks.js';
import { getLocalImage, putLocalImage, removeLocalImage } from '../lib/imageStore.js';
import { useLocalImageUrls } from '../features/pick/useLocalImageUrls.js';
import styles from './pageStyles.module.css';

/**
 * 비로그인 내 목록.
 *
 * 서버를 전혀 쓰지 않는다 — 이 브라우저에만 저장된다. "Try First, Login Later"의
 * 앞부분이고, 저장하고 싶어지는 순간에만 로그인을 요구한다.
 */
export function AnonymousPage() {
  const { t, language } = useI18n();
  const { status } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [picks, setPicks] = useState<LocalPick[]>(() => readLocalPicks());
  /* IndexedDB의 Blob을 화면에 띄울 objectURL로 바꿔 둔다 */
  const imageUrls = useLocalImageUrls(picks);
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  // 삭제는 되돌릴 수 없다. 카드가 촘촘히 붙어 있어 오클릭도 쉽다.
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCatalog(language)
      .then((loaded) => {
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [language]);

  // 이미 로그인한 사람이 이 화면에 오면 자기 목록으로 보낸다
  useEffect(() => {
    if (status === 'authenticated') {
      navigate('/me', { replace: true });
    }
  }, [status, navigate]);

  const flow = usePickFlow({
    existingPicks: picks,
    resolveImageUrl: (categoryId) => imageUrls.get(categoryId) ?? null,
    onSave: async (draft) => {
      // 비로그인이라 올릴 곳이 없다 — 사진은 브라우저에 담고, 목록에는
      // "사진이 있다"는 사실만 남긴다. 로그인할 때 그때 올린다.
      if (draft.imageBlob !== null) {
        await putLocalImage(draft.categoryId, draft.imageBlob);
      }
      const hasImage =
        draft.imageBlob !== null ||
        (draft.keptImageUrl !== null && (await getLocalImage(draft.categoryId)) !== null);
      if (!hasImage) {
        await removeLocalImage(draft.categoryId);
      }
      setPicks(
        putLocalPick({
          categoryId: draft.categoryId,
          itemName: draft.itemName,
          hasImage,
          ...(draft.itemDescription === undefined
            ? {}
            : { itemDescription: draft.itemDescription }),
        }),
      );
      toast.show(t('picks.saved'));
    },
    onRequestDelete: setPendingDelete,
  });

  return (
    <Layout>
      <div className={styles.head}>
        <div className={styles.headRow}>
          <div>
            <h1 className={styles.title}>{t('anonymous.title')}</h1>
          </div>
          {/*
            머리글에 "추가"를 두지 않는다. 격자 끝의 + 칸이 같은 일을 하고,
            둘 다 보이면 같은 버튼이 한 화면에 두 번 있는 꼴이다. 로그인한
            뒤의 내 목록도 + 칸만 쓴다 — 여기만 다르면 로그인하는 순간
            손이 기억한 자리에서 버튼이 바뀐다.
          */}
        </div>
      </div>

      {picks.length === 0 ? (
        /* 비어 있어도 같은 격자, 같은 추가 칸 — 로그인 뒤 화면과 같다 */
        <>
          <p className={styles.emptyText}>{t('anonymous.empty')}</p>
          <PickGrid>
            <PickGridAddCell label={t('picks.addOne')} onClick={flow.openSearch} />
            <PickGridGhostCell />
            <PickGridGhostCell />
          </PickGrid>
        </>
      ) : (
        <>
          <SortablePickGrid
            order={picks.map((pick) => pick.categoryId)}
            onReorder={(next) => setPicks(reorderLocalPicks(next))}
          >
            <PickGridAddCell label={t('picks.addOne')} onClick={flow.openSearch} />
            {picks.map((pick) => {
              const name =
                catalog === null ? pick.categoryId : categoryName(catalog, pick.categoryId);
              return (
                <PickGridCell
                  key={pick.categoryId}
                  item={{
                    categoryId: pick.categoryId,
                    categoryName: name,
                    itemName: pick.itemName,
                    ...(imageUrls.get(pick.categoryId) === undefined
                      ? {}
                      : { imageUrl: imageUrls.get(pick.categoryId) }),
                  }}
                  label={`${name} — ${pick.itemName}`}
                  sortable
                  onClick={() => flow.openEdit(pick.categoryId)}
                />
              );
            })}
          </SortablePickGrid>
        </>
      )}

      {/*
        로그인 안내는 **고른 게 있을 때만.**

        예전에는 비어 있어도 보여줬다. "이거 저장되나?"를 하나 고른 뒤에야
        알려주면 그때까지 모르고 쓴다는 이유였는데, 이제 헤더에 로그인이
        늘 있어서 그 질문은 거기서 답이 된다. 남은 건 고른 게 없는데
        "저장하시겠어요?"라고 묻는 모양새와, 같은 화면에 로그인 버튼이
        두 개 보이는 것뿐이었다.

        고른 게 있을 때의 이 안내는 성격이 다르다 — 헤더의 로그인이 그냥
        입구라면, 여기는 **내가 방금 고른 것들**을 두고 하는 말이다.
      */}
      {picks.length > 0 ? (
        <div className={styles.saveBar}>
          <span className={styles.saveBarText}>
            <strong>{t('picks.count', { count: picks.length })}</strong>
            {t('anonymous.saveLead')}
          </span>
          <Button variant="solid" fullWidth onClick={() => navigate('/signin')}>
            {t('anonymous.saveCta')}
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        destructive
        confirmLabel={t('common.delete')}
        title={t('picks.deleteConfirm', {
          category:
            pendingDelete !== null && catalog !== null ? categoryName(catalog, pendingDelete) : '',
        })}
        body={t('picks.deleteConfirmBody')}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) {
            setPicks(removeLocalPick(pendingDelete));
            void removeLocalImage(pendingDelete);
          }
          setPendingDelete(null);
        }}
      />

      {flow.element}
    </Layout>
  );
}
